/**
 * @typedef {import('./index.js').Store<State, Getters>} Store
 */
/**
 * @template T
 * @typedef {import('./index.js').GetterFunction<T, State, Getters>} GetterFunction<T>
 */
/**
 * @template T
 * @template R
 * @typedef {import('./index.js').ActionFunction<T, R, State, Getters>} ActionFunction<T,R>
 */
/**
 * @typedef Country
 * @property {string} Name
 * @property {string} Code
 * @property {string} IsoCode
 * @property {string} CallingCode
 * @property {Array<State>} States
 */
/**
 * @typedef State
 * @property {string} Name
 * @property {string} FullName
 * @property {string} Abbreviation
 * @property {string} CountryIsoCode
 */
import { isSitecore, isXPE } from '@utils/api-helpers'
import { getQueryParamByName } from '@utils/get-query-parameters'
/**
 * @module Store:Global
 * @description Global features for the application
 */
export default {
  namespaced: true,
  /** Global State
   * @typedef {Object} State
   * @property {boolean} isSitecore - Returns true if the page is being loaded in Preview or Editing mode in Sitecore
   * @property {boolean} isXPE - Returns true if the page is being loaded in Editing mode in Sitecore
   * @property {string} lang - Language the page is being loaded
   * @property {string} dir - Returns true if the direction of text if RTL (Right to Left)
   * @property {boolean} sidebarOpened - Is the sidebar opened?
   * @property {Array<Country>} countries - Array of country data
   * @property {boolean} isCalifornia - Is user from California (US)?
   * @property {boolean} isChile - Is user from Chile (CL)?
   * @property {Array<string>} propertiesWithTax - Array of properties that have taxes
   * @property {boolean} isFanClubBooking - Is user a Travel Advisor?
   */
  /** @type {State} */
  state: {
    isSitecore,
    isXPE,
    lang: document.querySelector('html').getAttribute('lang'),
    dir: document.querySelector('html').getAttribute('dir'),
    sidebarOpened: false,
    countries: [],
    isCalifornia: false,
    isChile: false,
    isFanClubBooking: false,
    propertiesWithTax: [],
  },
  /** Global Getters
   * @typedef {Object} Getters
   * @property {GetterFunction<boolean>} isRtl - Return true is language is RTL (Right to Left)
   */
  /** @type {Getters} */
  getters: {
    isRtl: (state) => state.dir === 'rtl',
    isApp: () => getQueryParamByName(window.location.href, 'origin') === 'app'
  },
  /** Mutations */
  mutations: {
    TOGGLE_SIDEBAR(/** @type {State} */
      state, toggleState
    ) {
      if (state.isXPE === true) return

      if (typeof toggleState === 'boolean') {
        state.sidebarOpened = toggleState
      } else {
        state.sidebarOpened = !state.sidebarOpened
      }
    },
    REGISTER_COUNTRIES_DATA(/** @type {State} */
      state, countries = []
    ) {
      state.countries = countries
    },
    UPDATE_IS_CALIFORNIA(/** @type {State} */
      state, isCalifornia = false
    ) {
      state.isCalifornia = isCalifornia
    },
    UPDATE_IS_FANCLUB_BOOKING(/** @type {State} */
      state, isFanClubBooking = false
    ) {
      state.isFanClubBooking = isFanClubBooking
    },
    UPDATE_IS_CHILE(/** @type {State} */
      state, isChile = false
    ) {
      state.isChile = isChile
    },
    UPDATE_PROPERTIES_WITH_TAX(/** @type {State} */
      state, propertiesWithTax = []
    ) {
      state.propertiesWithTax = propertiesWithTax
    },
  },
  /** Global Actions
   * @typedef {Object} Actions
   * @property {ActionFunction<boolean,void>} toggleSidebar - Open or close sidebar
   * @property {ActionFunction<Array<Country>,void>} registerCountriesData - Register a list of {@link Country} data
   */
  /** @type {Actions} */
  actions: {
    toggleSidebar({
      commit
    }, toggleState) {
      commit('TOGGLE_SIDEBAR', toggleState)
    },
    registerCountriesData({
      commit
    }, countries) {
      commit('REGISTER_COUNTRIES_DATA', countries)
    },
    updateIsCalifornia({
      commit
    }, isCalifornia) {
      commit('UPDATE_IS_CALIFORNIA', isCalifornia)
    },
    updateIsChile({
      commit
    }, isChile) {
      commit('UPDATE_IS_CHILE', isChile)
    },
    updateIsFanClubBooking({
      commit
    }, isFanClubBooking) {
      commit('UPDATE_IS_FANCLUB_BOOKING', isFanClubBooking)
    },
    updatePropertiesWithTax({
      commit
    }, propertiesWithTax) {
      commit('UPDATE_PROPERTIES_WITH_TAX', propertiesWithTax)
    },
  }
}
